import { AxiosError } from 'axios';
import { UseQueryResult } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';
import _ from 'underscore';

import { useAccountState } from 'contexts/account/account-context';
import { FIRST_FARM_SLOT, MARKET_SHORT_CODES } from 'views/benchmark-analytics/constants/benchmark-series';
import { DEFAULT_GROWTH_FILTERS } from '../constants/growth';
import { useGrowthBenchmark, useGrowthSiteData } from '../hooks/use-growth-series';
import { GrowthBenchmark } from '../types/growth-benchmark.type';
import { GrowthFarm, GrowthFarmSeries } from '../types/growth-farm.type';
import { GrowthFilters } from '../types/growth-filters.type';
import { GrowthPoint, GrowthResponse, GrowthSeries } from '../types/growth-series.type';
import { buildGrowthBenchmark } from '../utils/growth-benchmark';

type State = {
  filters: GrowthFilters;
  setFilters: (filters: GrowthFilters) => void;
  hasActiveFilters: boolean;
  farmOptions: GrowthFarm[];
  selectedFarmIds: string[];
  setSelectedFarmIds: (ids: string[]) => void;
  market: { shortCode: string; name: string };
  benchmarkQuery: UseQueryResult<GrowthResponse, AxiosError>;
  growth: GrowthBenchmark;
  farmSeries: GrowthFarmSeries[];
};

const GrowthContext = React.createContext<State | undefined>(undefined);

const GrowthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    account: { company },
  } = useAccountState();
  const [filters, setFilters] = useState<GrowthFilters>(DEFAULT_GROWTH_FILTERS);
  const [selectedFarmIds, setSelectedFarmIds] = useState<string[]>([]);

  // Page Clear resets filters only. Farms have their own Clear.
  const hasActiveFilters = !_.isEqual(filters, DEFAULT_GROWTH_FILTERS);

  const market = {
    shortCode: company ? (MARKET_SHORT_CODES[company.marketId] ?? company.marketName) : '',
    name: company?.marketName ?? '',
  };

  // Sorted by name, so a farm's slot (and color) never depends on what else is selected.
  const farmOptions = useMemo(
    () =>
      [...(company?.addressAccess ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((site, index) => ({
          globalSiteId: site.id,
          name: site.name,
          slot: FIRST_FARM_SLOT + index,
        })),
    [company?.addressAccess]
  );
  const selectedFarms = useMemo(
    () => farmOptions.filter((farm) => selectedFarmIds.includes(farm.globalSiteId)),
    [farmOptions, selectedFarmIds]
  );

  // From selectedFarms, not selectedFarmIds: sorted and valid for this company, so the query key ignores click order.
  const selectedSiteIds = selectedFarms.map((farm) => farm.globalSiteId);
  const benchmarkQuery = useGrowthBenchmark(company?.companyId, filters.dateRange);
  const siteDataQuery = useGrowthSiteData(company?.companyId, selectedSiteIds, filters.dateRange);

  const growth = useMemo(() => {
    const pointsKey = filters.variant === 'fullCycle' ? 'fullCyclePoints' : 'finalStagePoints';
    const points = (series: GrowthSeries | undefined): GrowthPoint[] => series?.[pointsKey] ?? [];
    const benchmarkSeries = benchmarkQuery.data?.series ?? [];

    return buildGrowthBenchmark(
      {
        company: points(benchmarkSeries.find((series) => series.type === 'companyAverage')),
        market: points(benchmarkSeries.find((series) => series.type === 'marketAverage')),
        farms: selectedFarms.map((farm) =>
          points(siteDataQuery.data?.series.find((series) => series.globalSiteId === farm.globalSiteId))
        ),
      },
      filters.grouping
    );
  }, [benchmarkQuery.data, siteDataQuery.data, selectedFarms, filters.variant, filters.grouping]);

  const farmSeries = useMemo(
    () =>
      selectedFarms.map((farm, index): GrowthFarmSeries => {
        const values = growth.farms[index];

        if (!siteDataQuery.data) return { ...farm, values, status: siteDataQuery.isError ? 'noData' : 'loading' };
        return { ...farm, values, status: values.every((value) => value === null) ? 'noData' : 'ready' };
      }),
    [selectedFarms, siteDataQuery.data, siteDataQuery.isError, growth.farms]
  );

  return (
    <GrowthContext.Provider
      value={{
        filters,
        setFilters,
        hasActiveFilters,
        farmOptions,
        selectedFarmIds,
        setSelectedFarmIds,
        market,
        benchmarkQuery,
        growth,
        farmSeries,
      }}>
      {children}
    </GrowthContext.Provider>
  );
};

function useGrowthState() {
  const context = useContext(GrowthContext);
  if (context === undefined) {
    throw new Error('useGrowthState must be used within a GrowthProvider');
  }
  return context;
}

export { GrowthProvider, useGrowthState };
